/**
 * AiUploader — popup с file-uploader: drag-drop + click-to-pick.
 *
 *  Принимает CSV / TXT / MD / JSON / TSV (по списку из streamlit-версии).
 *  Лимит размера — 5 МБ на файл (мягкий, проверяется в onChange).
 *  Возвращает выбранные файлы родителю через onFiles. Сам не хранит state.
 */

import { useCallback, useRef, useState, type DragEvent } from 'react';
import { Icon, IconButton } from '@/shared/ui';

const ACCEPT = '.csv,.txt,.md,.json,.tsv';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

export interface AiUploaderProps {
  open: boolean;
  onClose: () => void;
  onFiles: (files: File[]) => void;
}

export function AiUploader({ open, onClose, onFiles }: AiUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isHover, setHover] = useState(false);

  const handleFiles = useCallback(
    (incoming: FileList | null) => {
      if (!incoming || incoming.length === 0) return;
      const valid: File[] = [];
      for (const file of Array.from(incoming)) {
        if (file.size > MAX_FILE_SIZE) continue;
        valid.push(file);
      }
      if (valid.length > 0) onFiles(valid);
      onClose();
    },
    [onFiles, onClose],
  );

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHover(false);
    handleFiles(e.dataTransfer.files);
  };

  if (!open) return null;

  return (
    <div className="card" style={{ marginBottom: 8, overflow: 'hidden' }}>
      <div
        className="row spread center"
        style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}
      >
        <span style={{ fontSize: 13, fontWeight: 700 }}>Прикрепить файл</span>
        <IconButton icon="X" small ghost tip="Закрыть" onClick={onClose} />
      </div>

      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setHover(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDragLeave={() => setHover(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className="col center jcenter gap8"
        style={{
          margin: 12,
          padding: 24,
          textAlign: 'center',
          borderRadius: 'var(--r-sm)',
          border: `1.5px dashed ${isHover ? 'var(--primary)' : 'var(--border-strong)'}`,
          background: isHover ? 'var(--primary-light)' : 'var(--surface-alt)',
          cursor: 'pointer',
          transition: 'background .16s, border-color .16s',
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary-dark)',
            background: 'var(--primary-100)',
          }}
        >
          <Icon name="Paperclip" size={20} />
        </span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          Перетащите файл или нажмите, чтобы выбрать
        </span>
        <span className="muted" style={{ fontSize: 12 }}>
          CSV, TXT, MD, JSON, TSV · до 5 МБ
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
}
